import type { ImageContent } from '@unifyapps/defs/blocks/Image/types';
import { ImageSrcEnum } from '@unifyapps/defs/blocks/Image/types';
import type { BlockType } from '@unifyapps/defs/types/block';
import type { AssetDescriptor } from '@unifyapps/network/asset-cache/types';
import {
  checkIsStaticUrl,
  extractExtensionFromPath,
  stableHash,
} from '@unifyapps/network/asset-cache/utils';
import {
  extractFileNameFromPath,
  resolveFileTypeFromUploadDetails,
} from '../utils/resolveFileTypeFromUploadDetails';

/**
 * Pure function extracted from `ImageBlockStateDefinition.getCachableAssets`
 * so it can be imported from a Node tsx script (no React, no React Native).
 *
 * Async to keep a forward-compatible signature — today every branch
 * resolves synchronously, but the shape lets us plug in HEAD-request
 * metadata sniffing later without re-typing every call site.
 *
 * UPLOAD content → fileName / fileType / mimeType come straight from
 *   `uploadDetails`.
 * URL content   → fileName + fileType derived from the URL itself;
 *   mimeType stays `undefined` (we don't HEAD).
 */
export function imageGetCachableAssets(block: BlockType): Promise<AssetDescriptor[] | undefined> {
  const content = block.component.content as ImageContent;
  const data = content?.data;

  if (data?.type === ImageSrcEnum.UPLOAD) {
    const uploadDetails = data.uploadDetails;
    if (!uploadDetails || !checkIsStaticUrl(uploadDetails.url)) return Promise.resolve(undefined);
    return Promise.resolve([
      {
        url: uploadDetails.url,
        fileName: uploadDetails.name || stableHash(uploadDetails.url),
        fileType: resolveFileTypeFromUploadDetails(uploadDetails),
        mimeType: uploadDetails.mimeType,
      },
    ]);
  }

  // URL-input case (data.type === URL OR legacy top-level `src`).
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  const url = data?.type === ImageSrcEnum.URL ? data.src : content?.src;
  if (!checkIsStaticUrl(url)) return Promise.resolve(undefined);
  return Promise.resolve([
    {
      url,
      fileName: extractFileNameFromPath(url) ?? stableHash(url),
      fileType: extractExtensionFromPath(url),
      mimeType: undefined,
    },
  ]);
}
